import { createContext, ReactNode, useContext, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { SESSION_KEY } from 'constants/index'
import { deriveUserRole, USER_ROLE } from 'constants/user-role'
import { decodeJwtPayload } from 'utils/jwt'

interface AuthContextType {
  userRole: USER_ROLE | null
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Role marker IDs are carried in the access token's payload (`role`, or `roles`).
const readPermissions = (): number[] => {
  const payload = decodeJwtPayload(sessionStorage.getItem(SESSION_KEY.TOKEN))
  const raw = payload?.role ?? payload?.roles
  if (raw === undefined || raw === null) return []
  return (Array.isArray(raw) ? raw : [raw]).map(Number).filter(Number.isFinite)
}

interface AuthContextProps {
  children: ReactNode
}

const AuthProvider: React.FC<AuthContextProps> = ({ children }) => {
  const { pathname } = useLocation()
  // Re-derive userRole on every navigation so login/logout transitions pick up sessionStorage changes.
  const userRole = useMemo(() => deriveUserRole(readPermissions()), [pathname])

  return <AuthContext.Provider value={{ userRole }}>{children}</AuthContext.Provider>
}

const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext)

  if (context === undefined) {
    throw new Error('Context not available')
  }

  return context
}

export { AuthProvider, useAuthContext }
